import { useEffect } from 'react';
import { captureError } from '@leagueplatform/observability';
import { initSalesforceMessaging } from '../embedded-chat';

/**
 * React hook to initialize Salesforce Messaging for Web.
 *
 * Automatically calls `initSalesforceMessaging()` when the component mounts.
 * Safe to use in multiple components; the underlying initialization is idempotent.
 *
 * **Usage:**
 * - Call in your app's root component for eager loading (chat available immediately)
 * - Call in a specific route/page component for lazy loading (chat loads on-demand)
 *
 * This hook is intended for simple "fire-and-forget" initialization. If you need
 * custom success/error handling, analytics, or conditional initialization, call
 * `initSalesforceMessaging()` directly inside your own `useEffect`, or pass a
 * boolean flag here to control when initialization should occur.
 *
 * @example
 * // In App.tsx with default error logging
 * function App() {
 *   useInitSalesforceMessaging();
 *   return <YourApp />;
 * }
 */
export const useInitSalesforceMessaging = (enabled = true): void => {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    initSalesforceMessaging().catch((err) => {
      captureError(
        err instanceof Error
          ? err
          : new Error(
              `Failed to load Salesforce Messaging script: ${String(
                (err as Event)?.type ?? err,
              )}`,
            ),
        {
          errorName: 'Salesforce Messaging Init Failure',
        },
      );
      // eslint-disable-next-line no-console
      console.error('Failed to initialize Salesforce Messaging:', err);
    });
  }, [enabled]);
};
